import axios from 'axios';

// Loud warning if the admin frontend is deployed WITHOUT a proper
// VITE_API_URL. Otherwise axios falls back to '/' — same host as the
// admin subdomain, which just serves the SPA index.html for every
// path including /api/*. That was the 2026-07-07 root cause of the
// "admin enters directly but shows no data" bug. Dev/local is fine
// because Vite proxies /api → Laravel; production without an env is
// broken by definition.
const configuredBaseUrl = import.meta.env.VITE_API_URL as string | undefined;
if (import.meta.env.PROD && !configuredBaseUrl) {
    // eslint-disable-next-line no-console
    console.warn(
        '[admin] VITE_API_URL is not set. The admin panel will send API requests to itself; ' +
        'set VITE_API_URL to the Laravel API host in .env.production before building.'
    );
}

const http = axios.create({
    baseURL: configuredBaseUrl || '/',
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

export async function csrf() {
    await http.get('/sanctum/csrf-cookie');
}

// TELL THE API WHICH LANGUAGE THIS CONSOLE IS BEING READ IN.
//
// The backend resolves the locale from X-Locale (then Accept-Language) in
// SetLocaleFromRequest, and this client sent neither: every __() refusal the
// console can trigger - the role gate, the sign-in refusals - came back in
// whatever language the BROWSER happened to ask for, not the one the operator
// picked with the language switcher. Read from localStorage rather than the
// Pinia store so the header is already right on the very first request, before
// the store hydrates (the login POST is exactly that request).
/**
 * ── A DATE IS A DAY, NOT AN INSTANT ─────────────────────────────────────
 *
 * Owner, 2026-09-22: «لما بختار تاريخ مثلا 01/01/2025 بيتحفظ في 31/12/2024،
 * بيرجع يوم. راجع النقطة دي لانها خطر جدا».
 *
 * A PrimeVue DatePicker hands back a `Date` at LOCAL midnight. `toISOString()`
 * converts that to UTC, and at UTC+02/+03 local midnight is 21:00 or 22:00 of
 * the day BEFORE - so 01/01/2025 is stored as 2024-12-31. On a licence expiry
 * that is a day of service; on an announcement it is the wrong window; and
 * nothing anywhere says the number changed.
 *
 * The tenant application has answered this in its own client since June; the
 * admin never did, and four screens were each calling `toISOString()` by hand.
 * Answered once here, in the request, so a screen added tomorrow inherits it
 * instead of remembering it.
 */
function aDateIsADay(v: any): any {
    if (v === null || v === undefined) return v;
    if (v instanceof Date) {
        // Local Y-M-D. Never toISOString(), which is the whole defect - and
        // never the UTC getters either, which shift the other way west of
        // Greenwich.
        const yyyy = v.getFullYear();
        const mm = String(v.getMonth() + 1).padStart(2, '0');
        const dd = String(v.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }
    if (typeof v === 'string') {
        // A date that already carries midnight loses the time, and nothing
        // that carries a real time is touched.
        const m = v.match(/^(\d{4}-\d{2}-\d{2})[T ]00:00:00(?:\.\d+)?Z?$/);
        return m ? m[1] : v;
    }
    if (Array.isArray(v)) return v.map(aDateIsADay);
    if (typeof v === 'object' && v.constructor === Object) {
        const out: any = {};
        for (const k of Object.keys(v)) out[k] = aDateIsADay(v[k]);
        return out;
    }
    return v;
}

http.interceptors.request.use((config) => {
    const locale = localStorage.getItem('locale') === 'ar' ? 'ar' : 'en';
    config.headers = config.headers ?? ({} as any);
    (config.headers as any)['X-Locale'] = locale;

    // Bodies and query strings alike - a report's «from» is as much a day as
    // a licence's expiry. Uploads are left alone.
    if (config.data && typeof config.data === 'object'
        && !(config.data instanceof FormData) && !(config.data instanceof Blob)
        && !(config.data instanceof ArrayBuffer)) {
        config.data = aDateIsADay(config.data);
    }
    if (config.params && typeof config.params === 'object'
        && !(config.params instanceof URLSearchParams)) {
        config.params = aDateIsADay(config.params);
    }
    return config;
});

http.interceptors.response.use(
    (r) => {
        // 204 No Content + 205 Reset Content are intentionally
        // body-less — Sanctum's /sanctum/csrf-cookie returns 204 with
        // no Content-Type header at all, which is normal. Was
        // triggering the "server misconfigured" branch below because
        // axios returns r.data === '' for empty bodies and the header
        // check saw the absent Content-Type as "not JSON". User
        // 2026-07-13 verbatim: "الخادم غير مهيأ بشكل صحيح" was firing
        // on every CSRF handshake in production. Short-circuit here
        // so the legitimate empty responses never fall into the guard.
        if (r.status === 204 || r.status === 205) return r;

        // Guard against SPA-fallback HTML being served as a 200 for an
        // /api/* OR /sanctum/* endpoint (common misconfig when the admin
        // panel is deployed to a subdomain without a Laravel API proxy).
        // Was 2026-07-07 root cause of "enters directly but shows no
        // data": HTML string was truthy → router assumed authenticated.
        // Widened 2026-07-09 to cover /sanctum/csrf-cookie too — the
        // csrf() call was silently accepting HTML which set no XSRF
        // cookie and guaranteed the next POST would fail.
        const ct = (r.headers?.['content-type'] || '').toString().toLowerCase();
        const url = (r.config?.url || '').toString();
        const isApiOrSanctum = url.startsWith('/api/') || url.startsWith('/sanctum/');
        // Only trip the guard when the body ACTUALLY contains something
        // non-JSON — an empty string (r.data === '') just means the
        // endpoint returned no content, which is fine.
        if (isApiOrSanctum && !ct.includes('application/json') && typeof r.data === 'string' && r.data.length > 0) {
            // Wrap the HTML body in an object so downstream `data?.message`
            // lookups yield a real, translatable diagnostic instead of
            // undefined. Login.vue branches on status 502 for exactly
            // this synthetic response.
            const err: any = new Error(
                'Non-JSON response from an /api/ or /sanctum/ endpoint — CORS or proxy misconfig'
            );
            err.response = {
                status: 502,
                data: {
                    message: 'server_misconfigured',
                    hint: 'The admin SPA received HTML from an API endpoint. Check VITE_API_URL, CORS, or the Apache/Nginx vhost.',
                    html_preview: r.data.slice(0, 200),
                },
            };
            return Promise.reject(err);
        }
        return r;
    },
    (error) => {
        // Skip the hard redirect if the user is already on /login —
        // otherwise a 401 from POST /api/admin/login wipes the error
        // banner via full reload and looks like "nothing happened".
        const onLogin = window.location.pathname.startsWith('/login');
        if (error.response?.status === 401 && !onLogin) {
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default http;
